
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroupDirective,
  NgForm,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router, RouterModule } from '@angular/router';
import { debounceTime, map } from 'rxjs';
import { SeoService } from 'src/app/services/seo.service';
import { passwordMatchValidator } from '../../custom/form-validators/password-match.validator';
import { ErrorStateMatcher } from '@angular/material/core';
import { AuthService } from 'src/app/services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';

export class CrossFieldErrorMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    return !!(
      control &&
      control.parent?.hasError('passwordMismatch') &&
      (control.dirty || control.touched)
    );
  }
}

@Component({
    selector: 'app-register',
    imports: [
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule
],
    templateUrl: './register.component.html',
    styleUrl: './register.component.css'
})
export class RegisterComponent implements OnInit {
  fb = inject(FormBuilder);
  seoService = inject(SeoService);
  authService = inject(AuthService);
  snackbar = inject(MatSnackBar);
  router = inject(Router);
  crossFieldErrorMatcher = new CrossFieldErrorMatcher();

  registerForm = this.fb.group(
    {
      data: this.fb.group({
        first_name: ['', Validators.required],
        last_name: ['', Validators.required],
        company_name: ['', Validators.required],

        email: ['', [Validators.required, Validators.email]],
        cell_phone: [
          '',
          [Validators.pattern('[- +()0-9]{10,}'), Validators.maxLength(14)],
        ],
        work_phone: [
          '',
          [Validators.pattern('[- +()0-9]{10,}'), Validators.maxLength(14)],
        ],
        address_mail: ['', Validators.required],
        address_mail_line2: [''],
        address_mail_city: ['', Validators.required],
        address_mail_postal_code: ['', Validators.required],
        address_mail_province: ['', Validators.required],
        address_mail_country: ['Canada', Validators.required],
        fax_number: [],
        password: [
          '',
          [
            Validators.required,
            Validators.pattern(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$!%*?&])[A-Za-z\d@#$!%*?&]{8,}$/
            ),
          ],
        ],
      }),
      confirmPassword: ['', Validators.required],
    },
    { validators: passwordMatchValidator }
  );

  ngOnInit(): void {
    this.seoService.updateMetaTags([
      {
        name: 'description',
        content: `Create an Account to use the platform`,
      },
      { name: 'author', content: "Johnston's Connected" },
      { name: 'keywords', content: "Johnston's Connected Trading Platform" },
    ]);
    this.seoService.updateTitle("Johnston's Connected Create Account Page");
  }

  onSubmit() {
    if (!this.registerForm.valid) {
      this.snackbar.open(`Please Check form fields`, null, {
        duration: 4300,
        panelClass: ['blue-snackbar'],
      });
      return;
    }
    this.authService.signUpUser(this.registerForm.get('data').value).subscribe({
      next: () => {
        this.snackbar.open('Account created successfully! Please login', null, {
          duration: 4300,
          panelClass: ['blue-snackbar'],
        });
        this.router.navigate(['/login']);
      },
      error: (err) => {
        console.error('Sign up error:', err);
        this.snackbar.open(`Couldn't sign up, error occurred`, null, {
          duration: 4300,
          panelClass: ['red-snackbar'],
        });
      },
    });
  }
}
